/* eslint react-refresh/only-export-components: ["error", { "allowConstantExport": true }] */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { clientConfig } from '@/config';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    // Use crypto.randomUUID if available, fallback to timestamp + random
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newToast = { ...toast, id };
    
    // StrictMode and parallel loaders can report the same failure more than
    // once. Keep one visible notification per message so errors never cover
    // the controls the user needs to recover.
    setToasts(prev => prev.some(existing =>
      existing.type === toast.type &&
      existing.title === toast.title &&
      existing.message === toast.message
    ) ? prev : [...prev, newToast]);

    // Auto-remove toast after duration
    const duration = toast.duration || clientConfig.timeouts.toast;
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 bottom-3 z-50 space-y-2 sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-4 sm:w-[min(24rem,calc(100vw-2rem))]"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  const getToastStyles = () => {
  const baseStyles = 'pointer-events-auto w-full p-4 rounded-lg shadow-[var(--shadow-2)] border bg-[var(--bg-card)] sm:max-w-sm';
    switch (toast.type) {
      case 'success':
        return `${baseStyles} bg-[var(--state-success)]/10 border-[var(--state-success)]/30 text-[var(--state-success)]`;
      case 'error':
        return `${baseStyles} bg-[var(--state-danger)]/10 border-[var(--state-danger)]/30 text-[var(--state-danger)]`;
      case 'warning':
        return `${baseStyles} bg-[var(--state-warning)]/10 border-[var(--state-warning)]/30 text-[var(--state-warning)]`;
      case 'info':
        return `${baseStyles} bg-[var(--state-info)]/10 border-[var(--state-info)]/30 text-[var(--state-info)]`;
      default:
        return baseStyles;
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return 'ℹ';
    }
  };

  return (
    <div className={getToastStyles()}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <span className="text-lg font-bold">{getIcon()}</span>
        </div>
        <div className="ml-3 flex-1">
          <h4 className="text-sm font-semibold">{toast.title}</h4>
          {toast.message && (
            <p className="mt-1 text-sm opacity-90">{toast.message}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="ml-4 flex-shrink-0 text-lg font-bold opacity-60 hover:opacity-100"
        >
          ×
        </button>
      </div>
    </div>
  );
};
